import React, { useMemo, useState } from "react";
import { questionContext } from "./questionContext";

const S = {
  confirmed: { label: "확정 가능", icon: "✓", fg: "#166534", bg: "#f0fdf4", border: "#bbf7d0" },
  conditional: { label: "조건부 확정", icon: "△", fg: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe" },
  review_required: { label: "추가 검토 필요", icon: "!", fg: "#991b1b", bg: "#fff7f7", border: "#fecaca" },
  advisory: { label: "PROVE 검증 전", icon: "i", fg: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe" },
};

const fmt = (v) => {
  const d = String(v || "").replace(/\D/g, "");
  return d.length === 10 ? `${d.slice(0, 4)}.${d.slice(4, 6)}-${d.slice(6)}` : String(v || "-");
};

const FACT_LABELS = {
  material_base: "주된 재질",
  construction: "원단 조직",
  article_character: "제품 구조",
  use: "주된 용도",
  product_category: "제품 종류",
};

const VALUE_LABELS = {
  rubber: "가황고무",
  textile: "섬유재료",
  plastic: "플라스틱",
  leather: "가죽",
  metal: "금속",
  knit: "편물(니트)",
  woven: "직물(우븐)",
  nonwoven: "부직포",
  regenerated_cellulose: "재생 셀룰로오스",
};

const HS4_SCOPE = {
  3921: ["플라스틱의 판·시트류", "3921.14는 재생 셀룰로오스 재질"],
  4818: ["제지용 펄프·종이·셀룰로오스 워딩 제품", "화장지·타월·냅킨 등 종이계 완제품"],
  6307: ["그 밖의 섬유제 완제품", "직물·편물·부직포 등 섬유재료가 제품의 본질"],
};

const HS4_TITLE = {
  3921: "플라스틱 판·시트류",
  4818: "종이·셀룰로오스계 위생·가정용품",
  6307: "그 밖의 섬유제 완제품",
};

const requirementText = (requirement) => {
  const key = requirement?.fact_key || "필수 사실";
  const label = FACT_LABELS[key] || key.replace(/^composition\./, "구성비: ");
  const raw = String(requirement?.expected_value ?? "");
  const values = raw.split(",").map((v) => VALUE_LABELS[v.trim()] || v.trim()).filter(Boolean);
  if (!values.length) return label;
  const joiner = requirement?.operator === "in" ? " 중 하나" : "";
  return `${label}: ${values.join(" 또는 ")}${joiner}`;
};

const candidateMeta = (code, candidate = {}, proposedRequirements = [], option = {}, guidance = null) => {
  const h = String(code || "").replace(/\D/g, "").slice(0, 4);
  const registered = Array.isArray(candidate.selection_requirements)
    ? candidate.selection_requirements : proposedRequirements;
  const registeredConditions = registered.map(requirementText).filter(Boolean);
  const officialName = String(option.name_ko || option.popum || "").split("/양허유형")[0].trim();
  let title = officialName || HS4_TITLE[h] || "대안 세번";
  let fallbackConditions = HS4_SCOPE[h] || [];
  if (h === "4015") {
    title = "가황고무제 보호복";
    fallbackConditions = ["제품 본체 자체가 가황고무(경질고무 제외)"];
  } else if (h === "6113") {
    title = "고무·플라스틱 처리 편물제 보호복";
    fallbackConditions = ["본체가 편물(니트)", "그 편물에 고무·플라스틱을 도포 또는 적층"];
  } else if (h === "6210") {
    title = "코팅·적층 직물제 보호복";
    fallbackConditions = ["본체가 직물 또는 부직포", "고무·플라스틱 코팅·적층 원단으로 만든 의류"];
  }
  const conditions = [...new Set([...fallbackConditions, ...registeredConditions])];
  // 검증된 공식 프레임이 이 호 안에서 무엇으로 가르는지. ★질문은 확인된 사실이 아니므로
  // 필수 전제로 쓰지 않는다 — 선택 가능 여부는 그대로 등록된 전제에만 달려 있다.
  const nextCheck = guidance && guidance.question ? {
    question: guidance.question,
    labels: (Array.isArray(guidance.options) ? guidance.options : [])
      .map((o) => o && o.label).filter(Boolean),
  } : null;
  return {
    title,
    conditions,
    nextCheck,
    selectable: conditions.length > 0,
    fact: conditions.length
      ? `${conditions.join("; ")}인 제품입니다.`
      : `제품 특성이 ${fmt(code)} 세번의 요건에 해당하는지 아직 확인되지 않았습니다.`,
  };
};

const candidateOption = (code, options) => {
  const digits = String(code || "").replace(/\D/g, "");
  const list = Array.isArray(options) ? options : [];
  return list.find((item) => String(item?.hsk || "").replace(/\D/g, "") === digits)
    || list.find((item) => String(item?.hsk || "").replace(/\D/g, "").slice(0, 4) === digits.slice(0, 4))
    || {};
};

function normalize(message, productName) {
  const research = message.productResearch || {};
  const rv = message.reverseValidation || {};
  const facts = Array.isArray(research.facts) ? research.facts : [];
  const satisfied = Array.isArray(rv.satisfied) ? rv.satisfied : [];
  const unknown = Array.isArray(rv.unverified) ? rv.unverified : [];
  const conflicting = Array.isArray(rv.contradicted) ? rv.contradicted : [];
  const boundaries = Array.isArray(rv.sibling_assessments) ? rv.sibling_assessments : [];
  // 호별 갈림 질문 — 후보 판정에 붙어 오고, 선택된 호에는 사실 문장까지 붙어 온다.
  const frameGuidance = {};
  (Array.isArray(rv.candidate_assessments) ? rv.candidate_assessments : []).forEach((a) => {
    const h = String(a?.hs4 || a?.hsk || "").replace(/\D/g, "").slice(0, 4);
    if (h && a?.frame_guidance) frameGuidance[h] = a.frame_guidance;
  });
  if (rv.frame_guidance) {
    const h = String(rv.frame_guidance.hs4 || rv.proposed_hsk || "").replace(/\D/g, "").slice(0, 4);
    if (h) frameGuidance[h] = rv.frame_guidance;
  }
  const missing = Array.isArray(rv.missing_facts) ? rv.missing_facts : [];
  const inputAmbiguity = Array.isArray(message.inputAmbiguity) ? message.inputAmbiguity : [];
  const evidenceEmpty = facts.length === 0;
  let status = "conditional";
  if (inputAmbiguity.length > 1 || conflicting.length || evidenceEmpty || ["CONTRADICTED", "DEFINITION_UNAVAILABLE", "VALIDATION_ERROR"].includes(rv.status)) status = "review_required";
  else if (rv.status === "CONFIRMED" && !unknown.length && !missing.length) status = "confirmed";
  const recommendedHsk = research.candidate_hsk || rv.proposed_hsk || rv.confirmed_hsk || "";
  const alternatives = [...new Set([...boundaries.map((b) => b.target_hsk), ...inputAmbiguity.map((x) => x.hsk)].filter(Boolean))];
  const sourceRows = (research.sources || []).map((src) => ({
    ...src,
    legal: src.source_type === "official_ruling" ? "해외 참고사례이며 한국에서 직접적인 법적 구속력은 없음" : "상품 사실 확인 자료",
  }));
  const productFacts = [
    {
      item: "상품 종류",
      value: productName || research.identity || "분류 대상 상품",
      impact: "분류 대상의 기본 정체성",
      source: "사용자 입력",
      state: "사용자 제공",
    },
    ...(message.selectedFact ? [{
      item: "재질·제조 구조",
      value: message.selectedFact,
      impact: "후보 분기를 결정하는 사용자 확인 사실",
      source: "사용자 선택",
      state: "사용자 제공",
    }] : []),
    ...facts.map((f) => ({ item: f.key, value: String(f.value ?? ""), impact: f.evidence || "분류 사실", source: f.source_url ? "외부 자료" : "입력·추론", state: f.status || "UNKNOWN" })),
  ];
  return {
    product: { name: research.identity || productName || "분류 대상 상품" },
    decision: {
      recommendedHsk, officialDescription: rv.official_description || "공식 품명 확인 필요", status,
      confidence: null, alternativeCodes: alternatives, issueCount: missing.length + unknown.length + conflicting.length + (inputAmbiguity.length > 1 ? 1 : 0),
      summary: research.summary || rv.review_opinion || "확인된 상품 사실과 세번 요건을 대조한 검토 결과입니다.",
    },
    productFacts,
    reverseVerification: [
      ...satisfied.map((x) => ({ ...x, result: "satisfied" })),
      ...unknown.map((x) => ({ ...x, result: "unknown" })),
      ...conflicting.map((x) => ({ ...x, result: "conflicting" })),
    ],
    boundaries, missing, sources: sourceRows, inputAmbiguity,
    proposedRequirements: Array.isArray(rv.selection_requirements) ? rv.selection_requirements : [],
    frameGuidance,
    // 후보를 가르는 축으로 고른 질문 — 문언은 그 축을 다루는 공식 프레임에서 온다.
    askedQuestion: questionContext(rv),
    candidateOptions: Array.isArray(message.candidateOptions) ? message.candidateOptions : [],
  };
}

const cell = { padding: "10px 11px", borderBottom: "1px solid #edf0f3", verticalAlign: "top", fontSize: 12.5, lineHeight: 1.5 };
const th = { ...cell, color: "#4b5563", background: "#f8fafc", fontWeight: 700, whiteSpace: "nowrap" };

export default function ClassificationResultReport({ message, productName, onAsk, onHsk, onSelectCandidate, advisoryMode = "", onOpenProve }) {
  const r = useMemo(() => normalize(message, productName), [message, productName]);
  const [openStep, setOpenStep] = useState(null);
  const webAdvisory = advisoryMode === "web";
  const state = webAdvisory ? S.advisory : S[r.decision.status];
  const copy = (text) => navigator.clipboard?.writeText(text);
  const declaration = `HSK: ${fmt(r.decision.recommendedHsk)}\n상품명: ${r.product.name}\n주요 사실: ${r.productFacts.map((f) => `${f.item} ${f.value}`).join(", ")}`;
  const askFor = (m) => {
    const key = m.fact_key || m.key || "상품 정보";
    onAsk?.(key.includes("material") ? "제품의 정확한 재질과 각 재질의 구성 비율을 알려주세요." : `${key}에 관한 제품 자료를 알려주세요.`);
  };
  const anchors = [["conclusion", "결론"], ["facts", "상품 사실"], ["comparison", "후보 비교"], ["reasoning", webAdvisory ? "검증 흐름" : "분류 논리"], ["reverse", "역검증"], ...(!webAdvisory ? [["declaration", "신고 정보"]] : []), ["sources", "출처"]];
  return <article style={{ border: "1px solid #d9dee7", borderRadius: 10, background: "#fff", overflow: "hidden", boxShadow: "0 2px 8px rgba(15,23,42,.06)", margin: "10px 0 16px" }}>
    <nav style={{ display: "flex", gap: 4, padding: "9px 12px", borderBottom: "1px solid #e5e7eb", overflowX: "auto", position: "sticky", top: 0, zIndex: 2, background: "rgba(255,255,255,.96)" }}>
      {anchors.map(([id, label]) => <a key={id} href={`#cr-${id}`} style={{ color: "#4b5563", textDecoration: "none", fontSize: 11.5, padding: "5px 8px", whiteSpace: "nowrap" }}>{label}</a>)}
    </nav>
    <section id="cr-conclusion" style={{ padding: "22px 22px 18px", borderBottom: "1px solid #e5e7eb" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div><div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>{webAdvisory ? "AI 웹검증 참고 결과" : "품목분류 결과"}</div><h2 style={{ margin: 0, fontSize: 20, color: "#111827" }}>{r.product.name}</h2></div>
        <span style={{ padding: "7px 11px", borderRadius: 999, color: state.fg, background: state.bg, border: `1px solid ${state.border}`, fontWeight: 800, fontSize: 12 }}>{state.icon} {state.label}</span>
      </div>
      <div style={{ marginTop: 17, padding: "15px 16px", background: "#f8fafc", borderLeft: "4px solid #374151" }}>
        <div style={{ color: "#6b7280", fontSize: 11.5 }}>{webAdvisory ? "AI 웹검증 참고 후보" : r.decision.status === "review_required" ? "우선 검토 후보" : "추천 한국 HSK"}</div>
        <button onClick={() => onHsk?.(fmt(r.decision.recommendedHsk))} style={{ border: 0, padding: 0, background: "transparent", fontSize: 24, fontWeight: 850, color: "#111827", cursor: "pointer", fontFamily: "JetBrains Mono, monospace" }}>{fmt(r.decision.recommendedHsk)}</button>
        <div style={{ marginTop: 4, color: "#4b5563", fontSize: 12.5 }}>{r.decision.officialDescription}</div>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, color: "#4b5563" }}>대안 세번: {r.decision.alternativeCodes.length ? r.decision.alternativeCodes.map(fmt).join(", ") : "없음/미확인"}</span>
        <span style={{ fontSize: 12, color: "#4b5563" }}>검토 쟁점: {r.decision.issueCount}건</span>
        {webAdvisory ? (onOpenProve && 
          <button onClick={onOpenProve} style={{ marginLeft: "auto", border: "1px solid #1d4ed8", color: "#1d4ed8", background: "#fff", borderRadius: 6, padding: "6px 10px", cursor: "pointer", fontWeight: 700 }}>확정 검토 · 보고서</button>
        ) : (
          <>
            <button onClick={() => copy(declaration)} style={{ marginLeft: "auto", border: "1px solid #d1d5db", background: "#fff", borderRadius: 6, padding: "6px 10px", cursor: "pointer" }}>신고 정보 복사</button>
            <button onClick={() => window.print()} style={{ border: "1px solid #d1d5db", background: "#fff", borderRadius: 6, padding: "6px 10px", cursor: "pointer" }}>보고서 출력</button>
          </>
        )}
      </div>
    </section>
    {webAdvisory && <section style={{ padding: "13px 22px", borderBottom: "1px solid #bfdbfe", background: "#eff6ff", color: "#1e3a5f", fontSize: 12.5, lineHeight: 1.65 }}><strong>이 번호는 최종 판정이 아닙니다.</strong> Google 웹조사로 확인한 상품 설명을 기존 후보 생성과 역방향 요건 검증에 대조한 참고값입니다. PROVE 전진 결정트리가 같은 사실로 다시 계산해야 최종 판정 경로가 됩니다.</section>}
    <section style={{ padding: "17px 22px", borderBottom: "1px solid #e5e7eb" }}><h3 style={{ margin: "0 0 8px", fontSize: 14 }}>{webAdvisory ? "웹검증 요약" : "핵심 판정 요약"}</h3><p style={{ margin: 0, color: "#4b5563", fontSize: 13, lineHeight: 1.7 }}>{r.decision.summary}</p></section>
    <section id="cr-facts" style={{ padding: "18px 22px", borderBottom: "1px solid #e5e7eb" }}><h3 style={{ margin: "0 0 10px", fontSize: 14 }}>상품 사실</h3>
      <div style={{ overflowX: "auto" }}><table style={{ width: "100%", borderCollapse: "collapse" }}><thead><tr>{["항목", "확인된 사실", "분류 영향", "출처", "상태"].map((x) => <th key={x} style={th}>{x}</th>)}</tr></thead><tbody>{r.productFacts.length ? r.productFacts.map((f, i) => <tr key={i}><td style={cell}>{f.item}</td><td style={cell}>{f.value}</td><td style={cell}>{f.impact}</td><td style={cell}>{f.source}</td><td style={cell}>{f.state}</td></tr>) : <tr><td colSpan="5" style={cell}>확인된 상품 사실이 없어 추가 검토가 필요합니다.</td></tr>}</tbody></table></div>
    </section>
    <section id="cr-comparison" style={{ padding: "18px 22px", borderBottom: "1px solid #e5e7eb" }}><h3 style={{ margin: "0 0 10px", fontSize: 14 }}>후보 세번 비교</h3>
      {r.inputAmbiguity.length > 1 && <div style={{ padding: 12, marginBottom: 10, background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 7, fontSize: 12.5 }}><strong>입력 안정성 검증에서 서로 다른 류가 검출되었습니다.</strong><div style={{ marginTop: 6 }}>{r.inputAmbiguity.map((x) => `${x.query} → ${fmt(x.hsk)}`).join(" / ")}</div><div style={{ marginTop: 6 }}>띄어쓰기로 세번을 정하지 않고 재질과 제조 구조를 확인해야 합니다.</div></div>}
      {r.decision.status === "review_required" && (r.decision.recommendedHsk || r.boundaries.length) && <div style={{ marginBottom: 10, color: "#374151", fontSize: 13, lineHeight: 1.6 }}><strong>재질과 제조 구조를 선택하면 분류와 역검증을 다시 실행합니다.</strong><br />상품명만으로 한 세번을 확정하지 않습니다.</div>}
      {r.askedQuestion && <div style={{ marginBottom: 12, padding: "12px 13px", background: "#f0f7ff", border: "1px solid #cfe0f5", borderRadius: 7 }}>
        <div style={{ fontSize: 11.5, color: "#1d4ed8", fontWeight: 700, marginBottom: 5 }}>먼저 확인할 것</div>
        <div style={{ fontSize: 13, color: "#111827", lineHeight: 1.6 }}>{r.askedQuestion.question}</div>
        {r.askedQuestion.axis && r.askedQuestion.splitCount > 0 && <div style={{ marginTop: 6, fontSize: 11.5, color: "#4b5563" }}>
          {r.askedQuestion.axis}를 물어 후보 {r.askedQuestion.splitCount}개를 가릅니다
          {r.askedQuestion.wordingFrom ? ` · 문언 ${r.askedQuestion.wordingFrom}호` : ""}
        </div>}
        {r.askedQuestion.context && <div style={{ marginTop: 5, fontSize: 11.5, color: "#6b7280", lineHeight: 1.55 }}>
          이 호의 갈림: {r.askedQuestion.context}
        </div>}
      </div>}
      <div style={{ display: "grid", gap: 8 }}>
        {Array.from(new Map([...(r.decision.recommendedHsk ? [{ target_hsk: r.decision.recommendedHsk, resolution: "CANDIDATE" }] : []), ...r.boundaries].filter((b) => b.target_hsk).map((b) => [String(b.target_hsk).replace(/\D/g, "").slice(0, 4), b])).values()).map((b, i) => {
          const meta = candidateMeta(b.target_hsk, b,
            String(b.target_hsk).replace(/\D/g, "") === String(r.decision.recommendedHsk).replace(/\D/g, "")
              ? r.proposedRequirements : [], candidateOption(b.target_hsk, r.candidateOptions),
            r.frameGuidance[String(b.target_hsk).replace(/\D/g, "").slice(0, 4)]);
          const canSelect = meta.selectable && typeof onSelectCandidate === "function";
          return <button key={`${b.target_hsk}-${i}`} disabled={!canSelect} onClick={() => canSelect && onSelectCandidate({ hsk: b.target_hsk, ...meta })} style={{ padding: "14px 13px", border: "1px solid #d9dee7", borderRadius: 8, background: canSelect ? "#fff" : "#f8fafc", display: "grid", gridTemplateColumns: "120px 1fr auto", alignItems: "center", gap: 12, textAlign: "left", cursor: canSelect ? "pointer" : "not-allowed", opacity: canSelect ? 1 : 0.78 }}>
            <strong style={{ fontFamily: "JetBrains Mono, monospace" }}>{fmt(b.target_hsk)}</strong><span><b>{meta.title}</b>{meta.conditions.length ? <small style={{ display: "block", marginTop: 5, color: "#374151", lineHeight: 1.55 }}><b style={{ color: "#92400e" }}>필수 전제:</b> {meta.conditions.join(" · ")}</small> : meta.nextCheck ? <small style={{ display: "block", marginTop: 5, color: "#374151", lineHeight: 1.55 }}><b style={{ color: "#1d4ed8" }}>이 호의 갈림:</b> {meta.nextCheck.question}{meta.nextCheck.labels.length ? <span style={{ display: "block", marginTop: 4, color: "#6b7280" }}>{meta.nextCheck.labels.join(" · ")}</span> : null}</small> : <small style={{ display: "block", marginTop: 5, color: "#991b1b" }}>필수 전제 정보가 없어 직접 선택할 수 없습니다.</small>}</span><span style={{ color: canSelect ? "#4b5563" : "#9ca3af" }}>{canSelect ? "전제 확인 후 선택 →" : "선택 보류"}</span>
          </button>;
        })}
        {!r.decision.recommendedHsk && !r.boundaries.length && <div style={{ color: "#6b7280", fontSize: 12.5 }}>구조화된 대안 경계가 아직 등록되지 않았습니다.</div>}
      </div>
    </section>
    <section id="cr-reasoning" style={{ padding: "18px 22px", borderBottom: "1px solid #e5e7eb" }}><h3 style={{ margin: "0 0 10px", fontSize: 14 }}>{webAdvisory ? "AI 웹검증과 PROVE의 연결" : "분류 논리"}</h3>
      {(webAdvisory ? [
        ["공개 웹에서 상품 식별", "Google Search grounding으로 제조사·공식 자료를 찾고 사용자가 같은 상품인지 확인합니다."],
        ["참고 후보 생성", "확인된 상품 설명으로 기존 후보 생성기가 검토할 세번 후보를 제안합니다. 이 값은 PROVE 입력이나 확정값이 아닙니다."],
        ["역방향 요건 검증", "제안 후보의 정의·필수요건·형제 후보를 같은 상품 사실과 대조해 누락·상충을 표시합니다."],
        ["확정 검토 · 보고서", "확정 검토 작업공간에서 같은 웹조사를 다시 불러옵니다. 사용자가 사실을 확인하면 전진 결정트리가 HS4→HS6→KR10 경로를 독립 계산합니다."],
      ] : ["호의 결정", "소호·국내 세분류의 결정", "복합재료와 본질적 특성", "대안 세번 배제", "최종 세번 결정"].map((title) => [title, r.decision.summary])).map(([title, detail], i) => <div key={title} style={{ borderTop: "1px solid #edf0f3" }}><button onClick={() => setOpenStep(openStep === i ? null : i)} style={{ width: "100%", border: 0, background: "transparent", textAlign: "left", padding: "11px 2px", fontWeight: 700, cursor: "pointer" }}>{i + 1}. {title}<span style={{ float: "right" }}>{openStep === i ? "−" : "+"}</span></button>{openStep === i && <div style={{ padding: "0 2px 12px", color: "#4b5563", fontSize: 12.5, lineHeight: 1.65 }}>{detail}</div>}</div>)}
    </section>
    <section id="cr-reverse" style={{ padding: "18px 22px", borderBottom: "1px solid #e5e7eb" }}><h3 style={{ margin: "0 0 10px", fontSize: 14 }}>역방향 검증</h3>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>{[["충족", r.reverseVerification.filter((x) => x.result === "satisfied").length], ["미확인", r.reverseVerification.filter((x) => x.result === "unknown").length + r.missing.length], ["상충", r.reverseVerification.filter((x) => x.result === "conflicting").length]].map(([a,b]) => <span key={a} style={{ background: "#f3f4f6", padding: "5px 8px", borderRadius: 5, fontSize: 12 }}>{a} {b}개</span>)}</div>
      {r.reverseVerification.map((v, i) => <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 110px", gap: 12, padding: "9px 0", borderTop: "1px solid #edf0f3", fontSize: 12.5 }}><span>{v.requirement_id || v.fact_key || "세번 요건"}</span><strong>{v.result === "satisfied" ? "충족" : v.result === "conflicting" ? "상충" : "미확인"}</strong></div>)}
    </section>
    {r.missing.length > 0 && <section style={{ margin: "18px 22px", padding: 14, background: state.bg, border: `1px solid ${state.border}`, borderRadius: 8 }}><h3 style={{ margin: "0 0 8px", color: state.fg, fontSize: 14 }}>추가 확인사항 및 리스크</h3>{r.missing.map((m, i) => <button key={i} onClick={() => askFor(m)} style={{ display: "block", width: "100%", textAlign: "left", border: 0, background: "transparent", padding: "7px 0", cursor: "pointer", color: "#374151" }}>{m.fact_key || m.key}: {m.impact || "분류 경계 확인 필요"} → 질문하기</button>)}</section>}
    {!webAdvisory && <section id="cr-declaration" style={{ padding: "18px 22px", borderTop: "1px solid #e5e7eb", borderBottom: "1px solid #e5e7eb" }}><h3 style={{ margin: "0 0 10px", fontSize: 14 }}>신고용 정보</h3><pre style={{ whiteSpace: "pre-wrap", background: "#f8fafc", padding: 13, borderRadius: 7, margin: 0, fontFamily: "inherit", fontSize: 12.5, lineHeight: 1.7 }}>{declaration}</pre><button onClick={() => copy(declaration)} style={{ marginTop: 8, border: "1px solid #d1d5db", background: "#fff", borderRadius: 6, padding: "6px 10px", cursor: "pointer" }}>전체 복사</button></section>}
    <section id="cr-sources" style={{ padding: "18px 22px" }}><h3 style={{ margin: "0 0 10px", fontSize: 14 }}>출처 및 근거 자료</h3>{r.sources.length ? r.sources.map((s, i) => <div key={i} style={{ padding: "10px 0", borderTop: i ? "1px solid #edf0f3" : 0, fontSize: 12.5 }}><div style={{ fontWeight: 700 }}>{s.title || s.url}</div><div style={{ color: "#6b7280", margin: "3px 0" }}>{s.source_type} · {s.legal}</div>{s.url && <a href={s.url} target="_blank" rel="noreferrer">원문 보기</a>}</div>) : <div style={{ color: "#6b7280", fontSize: 12.5 }}>등록된 출처가 없습니다.</div>}</section>
  </article>;
}
